import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import emendaService, { resolveEmendaStatus, type EmendaDTO } from '../services/emendaService';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import { Permission, UserRole } from '../types/user.types';
import camaraAreaTrabalhoService, { type CamaraAreaTrabalhoDTO, type EmendaDevolvidaDTO } from '../services/camaraAreaTrabalhoService';
import funcionalidadesService, { type FuncionalidadeDTO } from '../services/funcionalidadesService';
import funcaoOrcamentariaService, { type FuncaoOrcamentariaDTO } from '../services/funcaoOrcamentariaService';
import councilorService, { type CouncilorDTO } from '../services/councilorService';
import WorkspaceOverview, { type WorkspaceMetric } from './dashboard/WorkspaceOverview';
import WorkspaceSection from './dashboard/WorkspaceSection';
import WorkspaceSectionGroup from './dashboard/WorkspaceSectionGroup';
import BoxEmendaVereador from './BoxEmendaVereador';
import Button from './ui/Button';
import { FIELD_CLASS } from './ui/fieldStyles';
import { Table, Tbody, Td, Th, Thead, Tr } from './ui/Table';
import CartaoFaleComEquipe from './CartaoFaleComEquipe';
import { toUpperText } from '../utils/formatters';
import CollapseToggle from './dashboard/CollapseToggle';

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));

const formatDate = (value?: string) => {
  if (!value) return 'Não informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const normalizeRuleText = (value?: string) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();

const inferCategoriaFromFuncaoCodigo = (codigo?: string) => {
  const normalized = String(codigo || '').trim();
  if (normalized === '10') return 'SAUDE';
  if (normalized === '12') return 'EDUCACAO';
  return null;
};

const inferCategoriasDaEmenda = (
  emenda: EmendaDTO,
  funcionalidadesByChave: Map<string, FuncionalidadeDTO[]>,
  funcionalidadesAgrupadas: Array<[string, FuncionalidadeDTO[]]>,
  funcoesByCodigo: Map<string, FuncaoOrcamentariaDTO>
): Set<'SAUDE' | 'EDUCACAO'> => {
  const categorias = new Set<'SAUDE' | 'EDUCACAO'>();
  const descricaoFuncao = emenda.funcaoCodigo ? funcoesByCodigo.get(emenda.funcaoCodigo)?.descricao : '';
  const categoriaNormalizada = normalizeRuleText(emenda.category || descricaoFuncao);
  const categoriaPorCodigo = inferCategoriaFromFuncaoCodigo(emenda.funcaoCodigo);

  if (categoriaNormalizada === 'SAUDE') categorias.add('SAUDE');
  if (categoriaNormalizada === 'EDUCACAO') categorias.add('EDUCACAO');
  if (categoriaNormalizada.includes('SAUDE')) categorias.add('SAUDE');
  if (categoriaNormalizada.includes('EDUCACAO')) categorias.add('EDUCACAO');
  if (categoriaPorCodigo === 'SAUDE') categorias.add('SAUDE');
  if (categoriaPorCodigo === 'EDUCACAO') categorias.add('EDUCACAO');

  if (categorias.size > 0) return categorias;

  const regraChave = emenda.funcaoCodigo || emenda.funcionalidadeChave;
  if (regraChave) {
    const regrasPorChave = funcionalidadesByChave.get(regraChave);
    regrasPorChave?.forEach((regra) => {
      if (regra.categoria === 'SAUDE' || regra.categoria === 'EDUCACAO') {
        categorias.add(regra.categoria);
      }
    });
  }

  if (categorias.size > 0) return categorias;

  const match = funcionalidadesAgrupadas.find(([chave, regras]) => {
    const descricao = regras[0]?.descricao;
    const chaveNormalizada = normalizeRuleText(chave);
    const descricaoNormalizada = normalizeRuleText(descricao);
    return categoriaNormalizada === chaveNormalizada
      || categoriaNormalizada === descricaoNormalizada;
  });

  match?.[1]?.forEach((regra) => {
    if (regra.categoria === 'SAUDE' || regra.categoria === 'EDUCACAO') {
      categorias.add(regra.categoria);
    }
  });

  return categorias;
};

const emendaStatusTone = (status?: string) => {
  const normalized = resolveEmendaStatus({ status, statusCicloVida: status }).toLowerCase();
  if (normalized.includes('conclu')) return 'border-success-line bg-success-soft text-success-text';
  if (normalized.startsWith('devolvid')) return 'border-danger-line bg-danger-soft text-danger-text';
  if (normalized.includes('análise') || normalized.includes('analise')) return 'border-warning-line bg-warning-soft text-warning-text';
  return 'border-line bg-surface-muted text-fg';
};

// Toda emenda devolvida/reprovada retorna ao legislativo; unificamos o rotulo exibido.
const displayStatusLabel = (rawStatus?: string): string => {
  const value = (rawStatus ?? '').trim();
  return value.toLowerCase().startsWith('devolvid') ? 'Devolvida ao legislativo' : value;
};

const renderSaldoCard = (saldo?: CamaraAreaTrabalhoDTO['saldos'][number] | null) => {
  if (!saldo) return null;

  return (
    <div className="rounded-2xl border border-info-line bg-info-soft p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-info-fg">
        Saldo disponível
      </p>
      <p className="mt-1 text-2xl font-bold text-info-fg">{formatCurrency(saldo.saldoDisponivel)}</p>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-info-fg">
        <div>
          <span className="opacity-70">Limite anual</span>
          <p className="font-semibold">{formatCurrency(saldo.limiteAnual)}</p>
        </div>
        <div>
          <span className="opacity-70">Empenhado ativo</span>
          <p className="font-semibold">{formatCurrency(saldo.totalEmpenhadoAtivo)}</p>
        </div>
        {!!saldo.totalRedistribuidoRecebido && (
          <div className="col-span-2">
            <span className="opacity-70">Recebido por redistribuição</span>
            <p className="font-semibold text-success-text">{formatCurrency(saldo.totalRedistribuidoRecebido)}</p>
          </div>
        )}
        <div className="col-span-2 mt-1 border-t border-info-line pt-2">
          <span className="opacity-70">Emendas em andamento</span>
          <p className="font-semibold">
            {saldo.totalEmendasAtivas ?? 0} emenda{(saldo.totalEmendasAtivas ?? 0) !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

const categoriaTone = (categoria?: string) =>
  categoria === 'EDUCACAO'
    ? 'border-info-line bg-info-soft text-info-fg'
    : 'border-success-line bg-success-soft text-success-text';

const planoStatusLabel = (status?: string) => {
  switch (status) {
    case 'APROVADO':
      return 'Aprovado';
    case 'REPROVADO':
      return 'Reprovado';
    case 'EM_ANALISE':
      return 'Em análise';
    case 'DEVOLVIDO_CORRECAO':
      return 'Devolvido';
    case 'CONCLUIDO':
      return 'Concluído';
    default:
      return 'Rascunho';
  }
};

const planoStatusTone = (status?: string) => {
  switch (status) {
    case 'APROVADO':
    case 'CONCLUIDO':
      return 'border-success-line bg-success-soft text-success-text';
    case 'REPROVADO':
    case 'DEVOLVIDO_CORRECAO':
      return 'border-danger-line bg-danger-soft text-danger-text';
    case 'EM_ANALISE':
      return 'border-warning-line bg-warning-soft text-warning-text';
    default:
      return 'border-line bg-surface-muted text-fg';
  }
};

const LegislativeWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emendas, setEmendas] = useState<EmendaDTO[]>([]);
  const [planos, setPlanos] = useState<PlanoTrabalho[]>([]);
  const [funcionalidades, setFuncionalidades] = useState<FuncionalidadeDTO[]>([]);
  const [funcionalidadesLoaded, setFuncionalidadesLoaded] = useState(false);
  const [funcoesOrcamentarias, setFuncoesOrcamentarias] = useState<FuncaoOrcamentariaDTO[]>([]);

  const isCamara = hasRole(UserRole.CAMARA);
  const isVereador = hasRole(UserRole.VEREADOR);
  const [areaTrabalhoData, setAreaTrabalhoData] = useState<CamaraAreaTrabalhoDTO | null>(null);
  const [cienciaLoading, setCienciaLoading] = useState(false);
  const [cienciaError, setCienciaError] = useState('');
  const [cienciaProcessing, setCienciaProcessing] = useState<string | null>(null);
  const [councilors, setCouncilors] = useState<CouncilorDTO[]>([]);
  const [redistribuirEmenda, setRedistribuirEmenda] = useState<EmendaDevolvidaDTO | null>(null);
  const [redistribuirDestino, setRedistribuirDestino] = useState('');
  const [redistribuirObservacao, setRedistribuirObservacao] = useState('');
  const [redistribuirSaving, setRedistribuirSaving] = useState(false);
  const [redistribuirError, setRedistribuirError] = useState('');
  const [collapsedBoxes, setCollapsedBoxes] = useState<Set<string>>(() => new Set([
    'relatorios-acompanhamento',
  ]));
  const [statusFilter, setStatusFilter] = useState<'todas' | 'legislativo'>('todas');
  const [reportTab, setReportTab] = useState<string>(() => (isCamara ? 'emendas' : 'saldo'));

  const toggleBox = (id: string) => setCollapsedBoxes(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const isBoxCollapsed = (id: string) => collapsedBoxes.has(id);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [emendasData, planosData] = await Promise.all([
          emendaService.listWithDetails().catch(() => []),
          planoService.listAll().catch(() => []),
        ]);
        if (!active) return;
        setEmendas(Array.isArray(emendasData) ? emendasData : []);
        setPlanos(Array.isArray(planosData) ? planosData : []);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Não foi possível carregar a área legislativa.');
        setEmendas([]);
        setPlanos([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isCamara && !isVereador) return;
    let active = true;
    const loadAreaTrabalho = async () => {
      try {
        setCienciaLoading(true);
        setCienciaError('');
        const data = await camaraAreaTrabalhoService.getAreaTrabalho();
        if (!active) return;
        setAreaTrabalhoData(data);
      } catch (err: any) {
        if (!active) return;
        setCienciaError(err?.response?.data?.error || 'Não foi possível carregar os dados de ciência.');
      } finally {
        if (active) setCienciaLoading(false);
      }
    };
    loadAreaTrabalho();
    return () => {
      active = false;
    };
  }, [isCamara, isVereador]);

  useEffect(() => {
    if (!isCamara && !isVereador) return;
    let active = true;
    const loadFuncionalidades = async () => {
      try {
        setFuncionalidadesLoaded(false);
        const data = await funcionalidadesService.listAtivas();
        if (!active) return;
        setFuncionalidades(Array.isArray(data) ? data : []);
      } catch {
        if (active) setFuncionalidades([]);
      } finally {
        if (active) setFuncionalidadesLoaded(true);
      }
    };
    loadFuncionalidades();
    return () => {
      active = false;
    };
  }, [isCamara, isVereador]);

  useEffect(() => {
    if (!isCamara && !isVereador) return;
    let active = true;
    const loadFuncoes = async () => {
      try {
        const data = await funcaoOrcamentariaService.list();
        if (!active) return;
        setFuncoesOrcamentarias(Array.isArray(data) ? data : []);
      } catch {
        if (active) setFuncoesOrcamentarias([]);
      }
    };
    loadFuncoes();
    return () => {
      active = false;
    };
  }, [isCamara, isVereador]);

  const handleDarCiencia = async (emendaId: string) => {
    setCienciaProcessing(emendaId);
    try {
      const updated = await camaraAreaTrabalhoService.registrarCiencia(emendaId);
      setAreaTrabalhoData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pendenteCiencia: prev.pendenteCiencia.filter((e) => e.id !== emendaId),
          comCiencia: [updated, ...prev.comCiencia],
        };
      });
    } catch (err: any) {
      setCienciaError(err?.response?.data?.error || 'Erro ao registrar ciência.');
    } finally {
      setCienciaProcessing(null);
    }
  };

  useEffect(() => {
    if (!isCamara) return;
    councilorService.list().then(setCouncilors).catch(() => setCouncilors([]));
  }, [isCamara]);

  const destinosElegiveis = useMemo(() => {
    if (!redistribuirEmenda) return [];
    return councilors
      .filter((c) => (c.esfera ?? 'MUNICIPAL') === 'MUNICIPAL')
      .filter((c) => !c.situacaoExcepcional)
      .filter((c) => c.councilorId !== redistribuirEmenda.councilorId)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [councilors, redistribuirEmenda]);

  const openRedistribuir = (item: EmendaDevolvidaDTO) => {
    setRedistribuirEmenda(item);
    setRedistribuirDestino('');
    setRedistribuirObservacao('');
    setRedistribuirError('');
  };

  const closeRedistribuir = () => setRedistribuirEmenda(null);

  const handleConfirmRedistribuir = async () => {
    if (!redistribuirEmenda || !redistribuirDestino) return;
    setRedistribuirSaving(true);
    setRedistribuirError('');
    try {
      const updated = await camaraAreaTrabalhoService.redistribuir(redistribuirEmenda.id, {
        parlamentarDestinoId: redistribuirDestino,
        observacao: redistribuirObservacao || undefined,
      });
      setAreaTrabalhoData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pendenteCiencia: prev.pendenteCiencia.filter((e) => e.id !== redistribuirEmenda.id),
          comCiencia: [updated, ...prev.comCiencia],
        };
      });
      closeRedistribuir();
    } catch (err: any) {
      setRedistribuirError(err?.response?.data?.error ?? 'Erro ao redistribuir emenda.');
    } finally {
      setRedistribuirSaving(false);
    }
  };

  const stats = useMemo(() => {
    const totalValor = emendas.reduce((acc, item) => acc + Number(item.value || 0), 0);
    const totalValorAtivo = emendas
      .filter((item) => !resolveEmendaStatus(item).toLowerCase().startsWith('devolvid'))
      .reduce((acc, item) => acc + Number(item.value || 0), 0);
    const devolvidas = emendas.filter((item) => resolveEmendaStatus(item).toLowerCase().startsWith('devolvid')).length;
    const concluidas = emendas.filter((item) => resolveEmendaStatus(item).toLowerCase().includes('conclu')).length;
    const parlamentares = new Set(emendas.map((item) => item.councilorId || item.councilorName).filter(Boolean)).size;
    const instituicoes = new Set(emendas.map((item) => item.institutionId || item.institutionName).filter(Boolean)).size;

    return {
      totalValor,
      totalValorAtivo,
      saldoDisponivel: totalValor - totalValorAtivo,
      devolvidas,
      concluidas,
      parlamentares,
      instituicoes,
    };
  }, [emendas]);

  const emendasFiltradas = useMemo(() => {
    if (statusFilter === 'todas') return emendas;
    return emendas.filter((item) => resolveEmendaStatus(item).toLowerCase().startsWith('devolvid'));
  }, [emendas, statusFilter]);

  const emendasRecentes = statusFilter === 'todas' ? emendas.slice(0, 6) : emendasFiltradas;

  const destinationStats = useMemo(() => {
    const funcionalidadesByChave = funcionalidades.reduce((acc, item) => {
      const current = acc.get(item.chave) ?? [];
      current.push(item);
      acc.set(item.chave, current);
      return acc;
    }, new Map<string, FuncionalidadeDTO[]>());

    const funcionalidadesAgrupadas = Array.from(funcionalidadesByChave.entries());
    const funcoesByCodigo = funcoesOrcamentarias.reduce((acc, item) => {
      acc.set(item.codigo, item);
      return acc;
    }, new Map<string, FuncaoOrcamentariaDTO>());

    return emendas.reduce(
      (acc, emenda) => {
        const categorias = inferCategoriasDaEmenda(emenda, funcionalidadesByChave, funcionalidadesAgrupadas, funcoesByCodigo);

        if (categorias.size === 0) {
          acc.semConfiguracao += 1;
          return acc;
        }

        const value = Number(emenda.value || 0);
        if (categorias.has('SAUDE')) acc.saude += value;
        if (categorias.has('EDUCACAO')) acc.educacao += value;
        return acc;
      },
      { saude: 0, educacao: 0, semConfiguracao: 0 }
    );
  }, [emendas, funcionalidades, funcoesOrcamentarias]);

  const percentualObrigatorioStats = useMemo(() => {
    const funcionalidadesByChave = funcionalidades.reduce((acc, item) => {
      const current = acc.get(item.chave) ?? [];
      current.push(item);
      acc.set(item.chave, current);
      return acc;
    }, new Map<string, FuncionalidadeDTO[]>());

    const funcionalidadesAgrupadas = Array.from(funcionalidadesByChave.entries());
    const funcoesByCodigo = funcoesOrcamentarias.reduce((acc, item) => {
      acc.set(item.codigo, item);
      return acc;
    }, new Map<string, FuncaoOrcamentariaDTO>());

    const aplicado = (emendas ?? []).reduce(
      (acc, emenda) => {
        const status = resolveEmendaStatus(emenda).toLowerCase();
        if (status.startsWith('devolvid') || status.includes('reprov')) return acc;

        const categorias = inferCategoriasDaEmenda(emenda, funcionalidadesByChave, funcionalidadesAgrupadas, funcoesByCodigo);
        if (categorias.size === 0) return acc;

        const value = Number(emenda.value || 0);
        if (categorias.has('SAUDE')) acc.saudeValor += value;
        if (categorias.has('EDUCACAO')) acc.educacaoValor += value;

        return acc;
      },
      { saudeBase: 0, educacaoBase: 0, saudeValor: 0, educacaoValor: 0 }
    );
    const obrigatorio = funcionalidades.reduce(
      (acc, item) => {
        const percentual = Number(item.percentual || 0);
        if (item.categoria === 'SAUDE') acc.saudePercentual = Math.max(acc.saudePercentual, percentual);
        if (item.categoria === 'EDUCACAO') acc.educacaoPercentual = Math.max(acc.educacaoPercentual, percentual);
        return acc;
      },
      { saudePercentual: 0, educacaoPercentual: 0 }
    );

    const limiteTotal = Number(areaTrabalhoData?.saldos?.[0]?.limiteAnual || 0);
    const saudeMetaValor = limiteTotal * (obrigatorio.saudePercentual / 100);
    const educacaoMetaValor = limiteTotal * (obrigatorio.educacaoPercentual / 100);

    return {
      limiteTotal,
      saudePercentual: obrigatorio.saudePercentual,
      educacaoPercentual: obrigatorio.educacaoPercentual,
      saudeMetaValor,
      educacaoMetaValor,
      saudeAplicadoValor: aplicado.saudeValor,
      educacaoAplicadoValor: aplicado.educacaoValor,
      saudeAtingidoPercentual: saudeMetaValor > 0 ? (aplicado.saudeValor / saudeMetaValor) * 100 : 0,
      educacaoAtingidoPercentual: educacaoMetaValor > 0 ? (aplicado.educacaoValor / educacaoMetaValor) * 100 : 0,
    };
  }, [areaTrabalhoData?.saldos, emendas, funcionalidades, funcoesOrcamentarias]);

  const summaryCards: WorkspaceMetric[] = isCamara
    ? [
        { label: 'Emendas visíveis', value: emendas.length, tone: 'neutral' },
        { label: 'Valor monitorado', value: formatCurrency(stats.totalValor), tone: 'neutral' },
        { label: 'Parlamentares', value: stats.parlamentares, tone: 'neutral' },
        { label: 'Planos vinculados', value: planos.length, tone: 'neutral' },
      ]
    : [
        { label: 'Minhas emendas', value: emendas.length, tone: 'neutral' },
        { label: 'Concluídas', value: stats.concluidas, tone: 'success' },
        { label: 'Devolvidas', value: stats.devolvidas, tone: 'warning' },
        { label: 'Instituições atendidas', value: stats.instituicoes, tone: 'neutral' },
        { label: 'Planos vinculados', value: planos.length, tone: 'neutral' },
      ];

  type ItemCiencia = NonNullable<typeof areaTrabalhoData>['pendenteCiencia'][number];

  // Mesmos botões na tabela (tela larga) e nos cartões (celular).
  const renderAcoesCiencia = (item: ItemCiencia) => (
    <div className="flex flex-col items-stretch gap-1.5">
      <Button variant="info" size="sm" disabled={cienciaProcessing === item.id} onClick={() => item.id && handleDarCiencia(item.id)}>
        {cienciaProcessing === item.id ? 'Aguarde...' : 'Dar ciência'}
      </Button>
      {isCamara && item.councilorSituacaoExcepcional && (
        <Button variant="warning-outline" size="sm" onClick={() => openRedistribuir(item)}>
          Redistribuir
        </Button>
      )}
    </div>
  );

  const renderPrioridadeCiencia = () => (
    cienciaLoading ? (
      <div className="px-5 py-10 text-center text-sm text-fg-muted">Carregando...</div>
    ) : cienciaError ? (
      <div className="px-5 py-6 text-center text-sm text-danger-text">{cienciaError}</div>
    ) : !areaTrabalhoData || areaTrabalhoData.pendenteCiencia.length === 0 ? (
      <div className="px-5 py-10 text-center text-sm text-fg-muted">
        Nenhuma emenda devolvida aguardando ciência.
      </div>
    ) : (
      <>
      {/* Celular: um cartão por emenda, com a ação visível sem rolar para o lado. */}
      <ul className="divide-y divide-line border-t border-line md:hidden">
        {areaTrabalhoData.pendenteCiencia.map((item) => (
          <li key={item.id} className="space-y-2 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-fg">{item.officialCode || 'Não informado'}</p>
                <p className="text-xs text-fg-subtle">
                  {[isCamara ? (item.councilorName || item.councilorId) : null, `Exercício ${item.exercicio || 'Não informado'}`].filter(Boolean).join(' · ')}
                </p>
              </div>
              <span className="shrink-0 text-sm font-medium tabular-nums text-fg">{formatCurrency(item.value)}</span>
            </div>
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${emendaStatusTone(item.statusCicloVida)}`}>
              {displayStatusLabel(item.statusCicloVida)}
            </span>
            {item.motivoDevolucao && <p className="text-sm text-fg-muted">{item.motivoDevolucao}</p>}
            {renderAcoesCiencia(item)}
          </li>
        ))}
      </ul>
      <div className="hidden max-h-[32rem] overflow-auto border-t border-slate-100 md:block">
        <Table density="comfortable">
          <Thead>
            <Tr className="border-b border-line">
              <Th className="text-fg">Código</Th>
              {isCamara && <Th className="text-fg">Vereador</Th>}
              <Th className="text-fg">Etapa</Th>
              <Th className="text-fg">Motivo</Th>
              <Th align="right" className="text-fg">Valor</Th>
              <Th align="center" className="text-fg">Ação</Th>
            </Tr>
          </Thead>
          <Tbody>
            {areaTrabalhoData.pendenteCiencia.map((item) => (
              <Tr key={item.id} className="border-b border-slate-100 last:border-b-0">
                <Td>
                  <div className="font-semibold text-fg">{item.officialCode || 'Não informado'}</div>
                  <div className="text-xs text-fg-muted">Exercício {item.exercicio || 'Não informado'}</div>
                </Td>
                {isCamara && (
                  <Td className="text-fg">
                    {item.councilorName || item.councilorId || 'Não informado'}
                  </Td>
                )}
                <Td>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${emendaStatusTone(item.statusCicloVida)}`}>
                    {displayStatusLabel(item.statusCicloVida)}
                  </span>
                </Td>
                <Td className="max-w-xs text-fg">{item.motivoDevolucao || '—'}</Td>
                <Td align="right" className="font-medium text-fg">{formatCurrency(item.value)}</Td>
                <Td align="center">
                  {renderAcoesCiencia(item)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
      </>
    )
  );

  const renderComCienciaTab = () => (
    !areaTrabalhoData || areaTrabalhoData.comCiencia.length === 0 ? (
      <div className="px-5 py-6 text-center text-sm text-fg-muted">Nenhuma emenda com ciência registrada.</div>
    ) : (
      <div className="max-h-96 overflow-auto">
        <Table density="comfortable">
          <Thead>
            <Tr className="border-b border-line">
              <Th className="text-fg">Código</Th>
              {isCamara && <Th className="text-fg">Vereador</Th>}
              <Th className="text-fg">Etapa</Th>
              <Th className="text-fg">Data da ciência</Th>
              <Th align="right" className="text-fg">Valor</Th>
            </Tr>
          </Thead>
          <Tbody>
            {areaTrabalhoData.comCiencia.map((item) => (
              <Tr key={item.id} className="border-b border-slate-100 last:border-b-0">
                <Td>
                  <div className="font-semibold text-fg">{item.officialCode || 'Não informado'}</div>
                  <div className="text-xs text-fg-muted">Exercício {item.exercicio || 'Não informado'}</div>
                </Td>
                {isCamara && (
                  <Td className="text-fg">
                    {item.councilorName || item.councilorId || 'Não informado'}
                  </Td>
                )}
                <Td>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${emendaStatusTone(item.statusCicloVida)}`}>
                    {displayStatusLabel(item.statusCicloVida)}
                  </span>
                </Td>
                <Td className="text-fg">
                  {item.redistribuidaPara ? (
                    <span className="text-warning-text">Redistribuída para {item.redistribuidaPara}</span>
                  ) : (
                    formatDate(item.dataCiencia)
                  )}
                </Td>
                <Td align="right" className="font-medium text-fg">{formatCurrency(item.value)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    )
  );

  const reportTabs = isCamara
    ? [
        { key: 'emendas', label: 'Emendas recentes' },
        { key: 'planos', label: 'Planos vinculados' },
        { key: 'porVereador', label: 'Por vereador' },
        { key: 'ciencia', label: 'Ciência registrada' },
      ]
    : [
        { key: 'saldo', label: 'Saldo & metas' },
        { key: 'emendas', label: 'Emendas recentes' },
        { key: 'planos', label: 'Planos vinculados' },
        { key: 'ciencia', label: 'Ciência registrada' },
      ];

  return (
    <div className="w-full space-y-6">
      <div>
        <WorkspaceOverview
          profile={isCamara ? 'Câmara Municipal' : 'Parlamentar'}
          description={isCamara
            ? 'Emendas devolvidas, ciências pendentes e acompanhamento legislativo.'
            : 'Suas emendas, devoluções e saldo disponível para indicação.'}
          metrics={summaryCards}
          loading={loading}
        />

      </div>

      {/* A pendência (dar ciência) vem antes do atalho de pedidos: primeiro o que espera o usuário. */}
      <WorkspaceSectionGroup>
        <WorkspaceSection
          color="amber"
          title="Emendas devolvidas pendentes de ciência"
          subtitle="Confirme o recebimento das devoluções. Após dar ciência, o valor retorna ao saldo disponível."
        >
          {renderPrioridadeCiencia()}
        </WorkspaceSection>
      </WorkspaceSectionGroup>

      {!isCamara && hasPermission(Permission.CREATE_SOLICITACAO_ALTERACAO) && <CartaoFaleComEquipe />}

      <div className="h-fit overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
        <button
          type="button"
          onClick={() => toggleBox('relatorios-acompanhamento')}
          className="flex w-full items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-fg-muted">
              {isCamara ? 'Relatórios, acompanhamento e atalhos' : 'Relatórios e acompanhamento'}
            </h2>
            <p className="mt-1 text-sm text-fg-muted">Consultas complementares, fora do caminho da ação principal.</p>
          </div>
          <CollapseToggle collapsed={isBoxCollapsed('relatorios-acompanhamento')} />
        </button>

        {!isBoxCollapsed('relatorios-acompanhamento') && (
          <div className="border-t border-line">
            <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-5 py-3">
              {reportTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setReportTab(tab.key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    reportTab === tab.key ? 'bg-indigo-600 text-white' : 'bg-surface-muted text-fg-muted hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {reportTab === 'saldo' && !isCamara && (
              <div className="grid gap-4 p-5 md:grid-cols-2">
                {areaTrabalhoData?.saldos?.[0] && renderSaldoCard(areaTrabalhoData.saldos[0])}
                {funcionalidadesLoaded && (percentualObrigatorioStats.saudePercentual > 0 || percentualObrigatorioStats.educacaoPercentual > 0) && (
                  <div>
                    <h3 className="text-sm font-bold text-fg">Percentuais obrigatórios</h3>
                    <p className="mt-1 text-sm text-fg-muted">
                      Meta calculada sobre o limite anual total e acompanhamento do quanto já foi atingido.
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {percentualObrigatorioStats.saudePercentual > 0 && (
                        <div className={`rounded-2xl border p-4 ${categoriaTone('SAUDE')}`}>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-80">Saúde</p>
                          <p className="mt-2 text-2xl font-bold">{percentualObrigatorioStats.saudePercentual.toFixed(2)}%</p>
                          <p className="mt-1 text-sm opacity-80">
                            Meta: {formatCurrency(percentualObrigatorioStats.saudeMetaValor)}
                          </p>
                          <p className="mt-1 text-sm opacity-80">
                            Aplicado: {formatCurrency(percentualObrigatorioStats.saudeAplicadoValor)}
                          </p>
                          <p className="mt-1 text-sm font-semibold opacity-90">
                            Atingido: {Math.min(percentualObrigatorioStats.saudeAtingidoPercentual, 999.99).toFixed(2)}%
                          </p>
                        </div>
                      )}
                      {percentualObrigatorioStats.educacaoPercentual > 0 && (
                        <div className={`rounded-2xl border p-4 ${categoriaTone('EDUCACAO')}`}>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-80">Educação</p>
                          <p className="mt-2 text-2xl font-bold">{percentualObrigatorioStats.educacaoPercentual.toFixed(2)}%</p>
                          <p className="mt-1 text-sm opacity-80">
                            Meta: {formatCurrency(percentualObrigatorioStats.educacaoMetaValor)}
                          </p>
                          <p className="mt-1 text-sm opacity-80">
                            Aplicado: {formatCurrency(percentualObrigatorioStats.educacaoAplicadoValor)}
                          </p>
                          <p className="mt-1 text-sm font-semibold opacity-90">
                            Atingido: {Math.min(percentualObrigatorioStats.educacaoAtingidoPercentual, 999.99).toFixed(2)}%
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {reportTab === 'emendas' && (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <p className="text-sm text-fg-muted">
                    {statusFilter === 'todas'
                      ? 'Amostra das emendas que este perfil pode acompanhar.'
                      : `Devolvidas ao legislativo (${emendasFiltradas.length}).`}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setStatusFilter((prev) => (prev === 'legislativo' ? 'todas' : 'legislativo'))}
                      aria-pressed={statusFilter === 'legislativo'}
                      title="Filtrar emendas devolvidas ao legislativo"
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                        statusFilter === 'legislativo'
                          ? 'border-red-400 bg-danger-soft text-danger-text'
                          : 'border-danger-line bg-danger-soft text-danger-text hover:bg-danger-line/40'
                      }`}
                    >
                      Devolvidas ao legislativo: <strong>{stats.devolvidas}</strong>
                    </button>
                    {statusFilter !== 'todas' && (
                      <Button variant="secondary" size="sm" onClick={() => setStatusFilter('todas')}>
                        Limpar filtro
                      </Button>
                    )}
                    {hasPermission(Permission.VIEW_EMENDAS) && (
                      <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard/emendas')}>
                        Ver todas
                      </Button>
                    )}
                  </div>
                </div>
                {loading ? (
                  <div className="px-5 py-10 text-center text-sm text-fg-muted">Carregando emendas...</div>
                ) : emendasRecentes.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-fg-muted">
                    {statusFilter === 'todas'
                      ? 'Nenhuma emenda disponível para este perfil.'
                      : 'Nenhuma emenda para o filtro selecionado.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto border-t border-slate-100">
                    <Table density="comfortable">
                      <Thead>
                        <Tr className="border-b border-line">
                          <Th className="text-fg">Código</Th>
                          <Th className="text-fg">Parlamentar</Th>
                          <Th className="text-fg">Instituicao</Th>
                          <Th className="text-fg">Status</Th>
                          <Th align="right" className="text-fg">Valor</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {emendasRecentes.map((item) => (
                          <Tr key={item.id} onClick={() => item.id && navigate(`/dashboard/emendas/${item.id}`)} className="border-b border-slate-100 last:border-b-0">
                            <Td>
                              <div className="font-semibold text-fg">{item.officialCode || 'Não informado'}</div>
                              <div className="text-xs text-fg-muted">Exercício {item.exercicio || 'Não informado'}</div>
                            </Td>
                            <Td className="text-fg">{item.councilorName || 'Não informado'}</Td>
                            <Td className="text-fg">{item.institutionName || 'Não informado'}</Td>
                            <Td>
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${emendaStatusTone(resolveEmendaStatus(item))}`}>
                                {displayStatusLabel(resolveEmendaStatus(item))}
                              </span>
                            </Td>
                            <Td align="right" className="font-medium text-fg">{formatCurrency(item.value)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {reportTab === 'planos' && (
              loading ? (
                <div className="px-5 py-10 text-center text-sm text-fg-muted">Carregando planos...</div>
              ) : planos.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-fg-muted">Nenhum plano de trabalho vinculado encontrado.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table density="comfortable">
                    <Thead>
                      <Tr className="border-b border-line">
                        <Th className="text-fg">Plano</Th>
                        <Th className="text-fg">Emenda</Th>
                        <Th className="text-fg">Modalidade</Th>
                        <Th className="text-fg">Status</Th>
                        <Th className="text-fg">Periodo</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {planos.slice(0, 6).map((item) => (
                        <Tr key={item.id} onClick={() => item.id && navigate(`/dashboard/plano/full/${item.id}`)} className="border-b border-slate-100 last:border-b-0">
                          <Td>
                            <div className="font-semibold text-fg">{item.titulo || 'Não informado'}</div>
                            <div className="text-xs text-fg-muted">{item.numeroProtocolo || 'Sem protocolo'}</div>
                          </Td>
                          <Td className="text-fg">{item.emendaCodigo || 'Não informada'}</Td>
                          <Td className="text-fg">{item.modalidade || 'Não informada'}</Td>
                          <Td>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${planoStatusTone(item.status)}`}>
                              {planoStatusLabel(item.status)}
                            </span>
                          </Td>
                          <Td className="text-fg">
                            {formatDate(item.periodoInicio)} a {formatDate(item.periodoFim)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
              )
            )}

            {reportTab === 'porVereador' && isCamara && (
              <div className="p-5">
                <BoxEmendaVereador isVereador={false} />
              </div>
            )}

            {reportTab === 'ciencia' && renderComCienciaTab()}
          </div>
        )}
      </div>

      {redistribuirEmenda && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-3xl bg-surface shadow-xl">
            <div className="border-b border-line px-6 py-5">
              <h2 className="text-lg font-bold text-fg">Redistribuir Emenda</h2>
              <p className="mt-1 text-sm text-fg-muted">
                {redistribuirEmenda.officialCode ?? redistribuirEmenda.id} — de{' '}
                {redistribuirEmenda.councilorName ?? redistribuirEmenda.councilorId}
              </p>
            </div>

            <div className="space-y-4 px-6 py-5">
              {redistribuirError && (
                <div className="rounded-xl border border-danger-line bg-danger-soft px-4 py-3 text-sm text-danger-text">
                  {redistribuirError}
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-semibold text-fg">Valor a redistribuir</label>
                <div className="w-full rounded-xl border border-line bg-surface-muted px-3 py-2 text-sm font-semibold text-fg">
                  {formatCurrency(redistribuirEmenda.value)}
                </div>
                <p className="mt-1 text-xs text-fg-muted">Valor total da emenda — redistribuição é sempre integral.</p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-fg">
                  Parlamentar destino <span className="text-red-500">*</span>
                </label>
                <select
                  value={redistribuirDestino}
                  onChange={(e) => setRedistribuirDestino(e.target.value)}
                  className={FIELD_CLASS}
                >
                  <option value="">Selecione...</option>
                  {destinosElegiveis.map((c) => (
                    <option key={c.councilorId} value={c.councilorId}>{c.fullName}</option>
                  ))}
                </select>
                {destinosElegiveis.length === 0 && (
                  <p className="mt-1 text-xs text-warning-text">Nenhum parlamentar municipal elegível como destino.</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-fg">Observação</label>
                <textarea
                  rows={2}
                  value={redistribuirObservacao}
                  onChange={(e) => setRedistribuirObservacao(toUpperText(e.target.value))}
                  className={FIELD_CLASS}
                  placeholder="Opcional"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-line px-6 py-4">
              <Button variant="secondary" onClick={closeRedistribuir}>
                Cancelar
              </Button>
              <Button variant="warning" disabled={redistribuirSaving || !redistribuirDestino} onClick={handleConfirmRedistribuir}>
                {redistribuirSaving ? 'Redistribuindo...' : 'Confirmar redistribuição'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegislativeWorkspace;
